import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

// ─── Data ────────────────────────────────────────────────────────────────────

const COUNTRIES = [
  { name: 'USA',     lat: 38,   lon: -97,  viewers: '280M' },
  { name: 'Brazil',  lat: -15,  lon: -47,  viewers: '180M' },
  { name: 'UK',      lat: 52,   lon: -1,   viewers: '68M'  },
  { name: 'India',   lat: 20,   lon: 77,   viewers: '500M' },
  { name: 'Japan',   lat: 36,   lon: 138,  viewers: '90M'  },
  { name: 'Germany', lat: 51,   lon: 10,   viewers: '74M'  },
  { name: 'France',  lat: 46,   lon: 2,    viewers: '62M'  },
  { name: 'Spain',   lat: 40,   lon: -4,   viewers: '48M'  },
  { name: 'Korea',   lat: 37,   lon: 127,  viewers: '52M'  },
  { name: 'China',   lat: 35,   lon: 105,  viewers: '400M' },
  { name: 'Mexico',  lat: 23,   lon: -102, viewers: '130M' },
  { name: 'Nigeria', lat: 9,    lon: 8,    viewers: '120M' },
  { name: 'Egypt',   lat: 26,   lon: 30,   viewers: '60M'  },
  { name: 'Russia',  lat: 61,   lon: 105,  viewers: '80M'  },
  { name: 'Aus',     lat: -27,  lon: 133,  viewers: '25M'  },
];

const GLOBE_RADIUS = 2;
const SIGNAL_RADIUS = 2.25;

function latLonToVec3(lat, lon, radius) {
  const phi   = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}

// Pre-compute country positions once
const COUNTRY_POSITIONS = COUNTRIES.map(c => latLonToVec3(c.lat, c.lon, GLOBE_RADIUS));

// ─── Signal arc geometry ──────────────────────────────────────────────────────

function buildArcPoints(fromVec, toVec, segments = 48) {
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    // Slerp-like: lerp then normalise and scale to SIGNAL_RADIUS
    const pt = new THREE.Vector3().lerpVectors(fromVec, toVec, t).normalize();
    // Arc lift: sin curve peaks at midpoint
    const lift = Math.sin(t * Math.PI) * 0.35;
    pt.multiplyScalar(SIGNAL_RADIUS + lift);
    points.push(pt);
  }
  return points;
}

// ─── Signal particle component ────────────────────────────────────────────────

function SignalArc({ fromIdx, toIdx, onComplete }) {
  const progressRef = useRef(0);
  const lineRef     = useRef();
  const dotRef      = useRef();

  const arcPoints = useMemo(
    () => buildArcPoints(COUNTRY_POSITIONS[fromIdx], COUNTRY_POSITIONS[toIdx]),
    [fromIdx, toIdx],
  );

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry().setFromPoints(arcPoints);
    return geo;
  }, [arcPoints]);

  useFrame((_, delta) => {
    progressRef.current = Math.min(1, progressRef.current + delta * 0.55);
    const p = progressRef.current;

    // Fade line based on progress
    if (lineRef.current) {
      lineRef.current.material.opacity = p < 0.1 ? p * 10 : p > 0.85 ? (1 - p) * 6.67 : 0.65;
    }

    // Move dot along arc
    if (dotRef.current) {
      const segIdx = Math.min(Math.floor(p * (arcPoints.length - 1)), arcPoints.length - 2);
      const frac   = p * (arcPoints.length - 1) - segIdx;
      const pos    = new THREE.Vector3().lerpVectors(arcPoints[segIdx], arcPoints[segIdx + 1], frac);
      dotRef.current.position.copy(pos);
      dotRef.current.material.opacity = lineRef.current ? lineRef.current.material.opacity : 0.9;
    }

    if (p >= 1 && onComplete) onComplete();
  });

  return (
    <group>
      <line ref={lineRef} geometry={geometry}>
        <lineBasicMaterial
          color="#7eb3ff"
          transparent
          opacity={0.65}
          linewidth={1}
        />
      </line>
      <mesh ref={dotRef}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshBasicMaterial color="#aad4ff" transparent opacity={0.9} />
      </mesh>
    </group>
  );
}

// ─── Country markers ──────────────────────────────────────────────────────────

function CountryMarkers({ activeIdx }) {
  return (
    <group>
      {COUNTRIES.map((_, i) => {
        const pos    = COUNTRY_POSITIONS[i];
        const active = i === activeIdx;
        return (
          <group key={i} position={[pos.x, pos.y, pos.z]}>
            <mesh>
              <sphereGeometry args={[active ? 0.065 : 0.04, 10, 10]} />
              <meshBasicMaterial
                color={active ? '#60aaff' : '#ffffff'}
                transparent
                opacity={active ? 1.0 : 0.25}
              />
            </mesh>
            {active && (
              <pointLight
                color="#4090ff"
                intensity={1.8}
                distance={1.5}
                decay={2}
              />
            )}
          </group>
        );
      })}
    </group>
  );
}

// ─── Globe mesh ───────────────────────────────────────────────────────────────

function GlobeWireframe() {
  const meshRef = useRef();

  useFrame((_, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.15;
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[GLOBE_RADIUS, 36, 36]} />
      <meshBasicMaterial
        wireframe
        color="#5588cc"
        opacity={0.09}
        transparent
      />
    </mesh>
  );
}

// ─── Inner globe (subtle glow) ────────────────────────────────────────────────

function GlobeGlow() {
  return (
    <mesh>
      <sphereGeometry args={[GLOBE_RADIUS * 0.98, 32, 32]} />
      <meshBasicMaterial color="#0a1a3a" transparent opacity={0.55} side={THREE.BackSide} />
    </mesh>
  );
}

// ─── Scene ────────────────────────────────────────────────────────────────────

function GlobeScene({ activeIdx, signals, onSignalDone, enableControls }) {
  const { gl } = useThree();

  useEffect(() => {
    gl.setClearColor(0x000000, 0);
  }, [gl]);

  return (
    <>
      <ambientLight intensity={0.15} />
      <pointLight position={[10, 10, 10]} intensity={0.3} color="#334466" />

      <GlobeGlow />
      <GlobeWireframe />
      <CountryMarkers activeIdx={activeIdx} />

      {signals.map(sig => (
        <SignalArc
          key={sig.id}
          fromIdx={sig.from}
          toIdx={sig.to}
          onComplete={() => onSignalDone(sig.id)}
        />
      ))}

      {/* OrbitControls attaches touch handlers to enable drag-to-rotate.
          On mobile those handlers eat the touchstart event when a user
          tries to swipe past the globe → page scroll dies. Only render
          on desktop (md+). Globe rotation is timer-driven inside
          GlobeWireframe anyway, so mobile users lose nothing visual. */}
      {enableControls && (
        <OrbitControls
          enablePan={false}
          enableZoom={false}
          rotateSpeed={0.5}
          enableDamping
          dampingFactor={0.08}
        />
      )}
    </>
  );
}

// ─── Illumination hook ────────────────────────────────────────────────────────

let _sigId = 0;

function useGlobeIllumination() {
  const [activeIdx,     setActiveIdx]     = useState(0);
  const [signals,       setSignals]       = useState([]);
  const [illuminatedCount, setIlluminated] = useState(0);

  const removeSignal = useCallback((id) => {
    setSignals(prev => prev.filter(s => s.id !== id));
  }, []);

  useEffect(() => {
    const tick = () => {
      const next = Math.floor(Math.random() * COUNTRIES.length);
      setActiveIdx(next);
      setIlluminated(prev => prev + 1);

      // Emit a signal from next to a random other country
      setSignals(prev => {
        if (prev.length >= 5) return prev;
        let to = Math.floor(Math.random() * COUNTRIES.length);
        if (to === next) to = (to + 1) % COUNTRIES.length;
        return [...prev, { id: ++_sigId, from: next, to }];
      });
    };

    const id = setInterval(tick, 1200);
    return () => clearInterval(id);
  }, []);

  return { activeIdx, signals, removeSignal, illuminatedCount };
}

// ─── Left panel ───────────────────────────────────────────────────────────────

function LeftPanel({ activeIdx, illuminatedCount }) {
  const country = COUNTRIES[activeIdx];

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '38%',
        height: '100%',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 2,
        pl: { md: 6, lg: 10 },
        pr: 3,
        pointerEvents: 'none',
      }}
    >
      {/* Section label */}
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.18em',
          color: 'rgba(96,170,255,0.7)',
          mb: 3,
          textTransform: 'uppercase',
        }}
      >
        05 — Global Reach
      </Typography>

      {/* Headline */}
      <Typography
        sx={{
          fontSize: 'clamp(2.2rem, 3.5vw, 4rem)',
          fontWeight: 900,
          color: '#ffffff',
          lineHeight: 0.95,
          letterSpacing: '-0.04em',
          mb: 0.5,
        }}
      >
        Your content,
      </Typography>
      <Typography
        sx={{
          fontSize: 'clamp(2.2rem, 3.5vw, 4rem)',
          fontWeight: 900,
          color: 'rgba(255,255,255,0.22)',
          lineHeight: 0.95,
          letterSpacing: '-0.04em',
          mb: 4,
        }}
      >
        in every market.
      </Typography>

      {/* Stat */}
      <Box
        sx={{
          mb: 3,
          px: 2.5,
          py: 1.5,
          border: '1px solid rgba(96,170,255,0.18)',
          borderRadius: 2,
          backdropFilter: 'blur(8px)',
          background: 'rgba(5,15,40,0.45)',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.7rem',
            fontWeight: 800,
            color: '#60aaff',
            letterSpacing: '-0.02em',
          }}
        >
          50+ countries
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', mt: 0.2 }}>
          reached with DubLabs
        </Typography>
      </Box>

      {/* Live counter */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#60aaff',
            boxShadow: '0 0 8px #60aaff',
            animation: 'pulse 1.2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%,100%': { opacity: 1 },
              '50%':      { opacity: 0.3 },
            },
          }}
        />
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <Typography
              sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.4 }}
            >
              <Box component="span" sx={{ color: '#60aaff', fontWeight: 700 }}>
                {country.name}
              </Box>
              {' '}— {country.viewers} viewers
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Illuminated counter */}
      <Typography
        sx={{
          mt: 2.5,
          fontSize: '0.7rem',
          color: 'rgba(255,255,255,0.25)',
          letterSpacing: '0.08em',
        }}
      >
        {illuminatedCount} markets illuminated
      </Typography>
    </Box>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function GlobeSection3D() {
  const { activeIdx, signals, removeSignal, illuminatedCount } = useGlobeIllumination();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        position: 'relative',
        height: '160vh',
      }}
    >
      {/* Sticky wrapper */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          background: '#020308',
          overflow: 'hidden',
        }}
      >
        {/* Desktop: left text panel */}
        <LeftPanel activeIdx={activeIdx} illuminatedCount={illuminatedCount} />

        {/* Canvas: right 60% on desktop, full on mobile.
            On mobile, pointerEvents: none makes the whole canvas
            container transparent to touches so page scroll works
            normally through it. Also disable r3f's event pipeline
            entirely on mobile so no listeners get attached at all. */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: { xs: '100%', md: '62%' },
            height: '100%',
            pointerEvents: { xs: 'none', md: 'auto' },
            touchAction: 'pan-y',
          }}
        >
          <Canvas
            camera={{ position: [0, 0, 5.5], fov: 45 }}
            style={{
              width: '100%',
              height: '100%',
              background: 'transparent',
              touchAction: 'pan-y',
              pointerEvents: isDesktop ? 'auto' : 'none',
            }}
            gl={{ antialias: true, alpha: true }}
            events={isDesktop ? undefined : () => ({ enabled: false, priority: 0 })}
          >
            <GlobeScene
              activeIdx={activeIdx}
              signals={signals}
              onSignalDone={removeSignal}
              enableControls={isDesktop}
            />
          </Canvas>
        </Box>

        {/* Mobile bottom overlay label */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'absolute',
            bottom: 40,
            left: 0,
            right: 0,
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 3,
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: 'rgba(96,170,255,0.6)',
              mb: 1,
              textTransform: 'uppercase',
            }}
          >
            05 — Global Reach
          </Typography>
          <Typography
            sx={{
              fontSize: 'clamp(1.6rem, 7vw, 2.5rem)',
              fontWeight: 900,
              color: '#fff',
              letterSpacing: '-0.04em',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            Your content,{' '}
            <Box component="span" sx={{ color: 'rgba(255,255,255,0.28)' }}>
              in every market.
            </Box>
          </Typography>
        </Box>

        {/* Subtle vignette on left edge (desktop) to blend into bg */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 0,
            left: 0,
            width: '45%',
            height: '100%',
            background: 'linear-gradient(to right, #020308 35%, transparent)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        {/* Vignette edges */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at center, transparent 40%, #020308 100%)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
}
